import { useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { formatAmountRounded, formatCount, formatCredited } from '../../utils/creditNoteFormat';
import { NO_REASON_CODE } from '../../types/creditNotes';
import type {
    CreditNoteReasonCatalogEntry,
    CreditNoteSummary,
    CreditNoteTrendReasonRow,
} from '../../types/creditNotes';
import { REASON_GROUPS, groupColor, groupOf } from './reasonGroups';

/**
 * Reasons & trends — why we credit, and whether that is moving.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * THE COVERAGE RATE IS THE HEADLINE, NOT DECORATION. "Do we know the reason at all?" is the
 * process question this page was commissioned to answer, and its answer is a number per company:
 * dbi and bin at 100 % out of the ERP, dch and dit at almost nothing. Every other figure on this
 * tab is a breakdown of the part that IS known, so it sits underneath.
 *
 * NO SECOND EVALUATION ANYWHERE. Every number rendered here comes out of `summary.trends`, which
 * the server computed from the same rows the list filters and the rule tiles count —
 * `trends.byReason[].documents` is literally `summary.byAppReason` handed through. The coupling
 * the plan demands of a tile ("what it counts is what its click returns") is not weaker for a
 * chart: a bar a reader clicks has to hand back the rows it was drawn from.
 *
 * "NO REASON RECORDED" IS A CATEGORY. It is ranked, stacked and legended like the ten real
 * codes, in a deliberate neutral grey. At dit it is the whole column; a chart that omitted those
 * rows would draw an empty year and call it clean.
 *
 * THE CURRENT MONTH IS DIMMED, NOT HIDDEN. A month in progress is not comparable with a closed
 * one and is also the month the reader most wants to see. Hiding it loses today's work; drawing
 * it like the others invites "why did September collapse?" on the third of the month.
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * ACCESSIBILITY. The six group colours were validated with the dataviz palette validator (light
 * and dark, adjacent-pair CVD, chroma, contrast — all checks pass); identity is never carried by
 * colour alone, because the legend, the direct labels and the table below all name the category
 * in words; and the table IS the accessible view of both charts, not a bonus.
 */

interface Props {
    summary: CreditNoteSummary;
    catalog: CreditNoteReasonCatalogEntry[];
    loading: boolean;
    /** Sets `appReasonCode` on the credit-notes tab and switches to it — the bar's own click. */
    onDrillToReason: (reasonCode: string) => void;
    onExport: () => Promise<void>;
}

type SortKey = 'reason' | 'documents' | 'value' | 'share';

const WIDTH_KEY = 'credit-notes-trends-col-widths';

interface Col {
    id: string;
    label: string;
    sort?: SortKey;
    align?: 'left' | 'right';
    width: number;
}

const COLS: Col[] = [
    { id: 'reason', label: 'Reason', sort: 'reason' as SortKey, width: 240 },
    { id: 'documents', label: 'Documents', sort: 'documents' as SortKey, align: 'right' as const, width: 110 },
    { id: 'value', label: 'Credited EUR', sort: 'value' as SortKey, align: 'right' as const, width: 140 },
    { id: 'share', label: 'Share of documents', sort: 'share' as SortKey, align: 'right' as const, width: 150 },
    { id: 'avg', label: 'Avg / document', align: 'right' as const, width: 140 },
];

function reasonLabel(catalog: CreditNoteReasonCatalogEntry[], code: string): string {
    if (code === NO_REASON_CODE) return 'No reason recorded';
    const entry = catalog.find(c => c.reasonCode === code);
    // `ReasonLabelDe` is NULL for all ten until Stefan writes them (open question 1). Until then
    // the ERP's own 20-character stub is the most truthful label available, and the page says
    // elsewhere that it is a stub rather than inventing a readable German sentence.
    return entry?.reasonLabelDe || entry?.erpCategoryRaw || code;
}

export function TrendsTab({ summary, catalog, loading, onDrillToReason, onExport }: Props) {
    const { trends } = summary;
    const [sort, setSort] = useState<SortState<SortKey>>({ key: 'value', direction: 'desc' });
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    const onSort = (key: SortKey) => {
        // Swallows the click that ENDS a resize drag, so finishing a drag never re-sorts.
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, key !== 'reason'));
    };

    const maxReasonValue = Math.max(1, ...trends.byReason.map(r => r.valueEur));
    const maxMonthValue = Math.max(1, ...trends.months.map(m => m.valueEur));

    /** The groups that actually occur, in the fixed order — never a colour assigned by rank. */
    const activeGroups = useMemo(() => {
        const present = new Set(trends.byReason.map(r => groupOf(r.reasonCode)));
        return REASON_GROUPS.filter(g => present.has(g.id));
    }, [trends.byReason]);

    const sortedRows = useMemo(() => {
        const rows = [...trends.byReason];
        const dir = sort.direction === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
            switch (sort.key) {
                case 'reason': return (a.sortOrder - b.sortOrder) * dir;
                case 'documents': return (a.documents - b.documents) * dir;
                case 'share': return (a.documents - b.documents) * dir;
                default: return (a.valueEur - b.valueEur) * dir;
            }
        });
        return rows;
    }, [trends.byReason, sort]);

    async function runExport() {
        setExporting(true);
        setExportError(null);
        try {
            await onExport();
        } catch (err) {
            setExportError(err instanceof Error ? err.message : 'Export failed.');
        } finally {
            setExporting(false);
        }
    }

    const share = (r: CreditNoteTrendReasonRow) =>
        trends.totals.documents ? (r.documents / trends.totals.documents) * 100 : 0;

    if (loading && trends.byReason.length === 0) {
        return (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading the figures…
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* ── Coverage: the process figure ─────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-sm font-semibold text-gray-900">Do we know the reason at all?</h2>
                    <div className="flex items-center gap-3 text-[11px] text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: '#2563eb' }} />
                            from the ERP
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: '#0d9488' }} />
                            recorded here
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-sm border border-gray-300" style={{ background: '#e5e7eb' }} />
                            no reason
                        </span>
                    </div>
                </div>
                <p className="mb-3 text-[11px] leading-4 text-gray-500">
                    Share of credit notes carrying a reason, over the period and companies selected on
                    the credit-notes tab — the same rows every other figure here is counted from.
                    dbi and bin carry it in the ERP; dch and dit are where the work is.
                </p>
                <p className="mb-3 text-[11px] leading-4 text-gray-500">
                    “ERP code, not mapped” counts documents carrying the BC standard{' '}
                    <span className="font-mono">Reason Code</span>. They are deliberately <b>not</b>{' '}
                    coverage: outside dbi and bin that field is a posting classification
                    (<span className="font-mono">SALE</span> at dfr,{' '}
                    <span className="font-mono">SALES</span> at dsp and dtr), and only dch ever used
                    it as a reason. The value is shown on each document; it is never turned into one
                    of the ten group codes.
                </p>

                <div className="space-y-2">
                    {trends.coverageByCompany.length === 0 && (
                        <p className="text-sm text-gray-500">No credit notes in this period.</p>
                    )}
                    {trends.coverageByCompany.map(c => {
                        const pct = (n: number) => (c.documents ? (n / c.documents) * 100 : 0);
                        return (
                            <div key={c.companyCode} className="grid grid-cols-[64px_minmax(0,1fr)_260px] items-center gap-3">
                                <span className="font-mono text-xs text-gray-700">{c.companyCode}</span>
                                {/* 2px surface gaps between segments, per the mark spec. */}
                                <span className="flex h-3.5 w-full overflow-hidden rounded bg-gray-100">
                                    {c.fromErp > 0 && (
                                        <span
                                            className="h-full"
                                            style={{ width: `${pct(c.fromErp)}%`, background: '#2563eb', marginRight: 2 }}
                                            title={`${c.fromErp} from the ERP`}
                                        />
                                    )}
                                    {c.fromUser > 0 && (
                                        <span
                                            className="h-full"
                                            style={{ width: `${pct(c.fromUser)}%`, background: '#0d9488', marginRight: 2 }}
                                            title={`${c.fromUser} recorded in the app`}
                                        />
                                    )}
                                </span>
                                <span className="text-right text-xs text-gray-700">
                                    <b className="font-semibold text-gray-900">
                                        {c.coveragePct === null ? '–' : `${c.coveragePct}%`}
                                    </b>
                                    <span className="ml-1.5 text-gray-500">
                                        {formatCount(c.fromErp + c.fromUser)} of {formatCount(c.documents)}
                                    </span>
                                    {/* The 8 % that is NOT counted, named where it would otherwise
                                        look like a defect. See `withErpCodeNotMapped`. */}
                                    {c.withErpCodeNotMapped > 0 && (
                                        <span
                                            className="ml-1.5 text-gray-400"
                                            title={`${c.withErpCodeNotMapped} of these documents carry the BC standard "Reason Code". It is shown on the document and never mapped into the group vocabulary — outside dbi and bin that field is a posting classification (SALE, SALES), not a reason.`}
                                        >
                                            +{formatCount(c.withErpCodeNotMapped)} ERP code, not mapped
                                        </span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {summary.documentsReasonEditableAndEmpty > 0 && (
                    <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12px] leading-4 text-amber-800">
                        <b>{formatCount(summary.documentsReasonEditableAndEmpty)}</b> credit notes can still
                        have a reason recorded in the app — that is the work left, not a defect. The ERP
                        does not enforce the field either: the same extension column on the purchase side
                        is filled on 25 of 192 documents.
                    </p>
                )}
            </section>

            {/* ── Ranking by reason ────────────────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h2 className="text-sm font-semibold text-gray-900">Why we credit</h2>
                <p className="mb-3 text-[11px] leading-4 text-gray-500">
                    Credited value per reason, EUR. Click a bar to see exactly those credit notes — the
                    count beside it is the number that filter returns.
                    {!trends.totals.valueEurIsComplete && (
                        <> Some documents have no monthly FX rate for their posting month; they are counted
                        but their value cannot be added, and the rows say so.</>
                    )}
                </p>
                <div className="space-y-1.5">
                    {trends.byReason.length === 0 && (
                        <p className="text-sm text-gray-500">No credit notes in this period.</p>
                    )}
                    {trends.byReason.map(r => (
                        <button
                            key={r.reasonCode}
                            onClick={() => onDrillToReason(r.reasonCode)}
                            className="grid w-full grid-cols-[190px_minmax(0,1fr)_180px] items-center gap-3 rounded px-1 py-0.5 text-left hover:bg-gray-50"
                            title={`Show the ${r.documents} credit notes with this reason`}
                        >
                            <span className="truncate text-[11px] text-gray-700">
                                {reasonLabel(catalog, r.reasonCode)}
                            </span>
                            <span className="block h-3.5 w-full">
                                <span
                                    className="block h-full rounded-r"
                                    style={{
                                        width: `${Math.max(1, (r.valueEur / maxReasonValue) * 100)}%`,
                                        background: groupColor(r.reasonCode),
                                    }}
                                />
                            </span>
                            <span className="text-right text-[11px] text-gray-600">
                                <b className="font-semibold text-gray-900">{formatAmountRounded(r.valueEur)}</b>
                                <span className="ml-1.5">{formatCount(r.documents)} docs</span>
                                {!r.valueEurIsComplete && <span className="ml-1 text-amber-700">·partial</span>}
                            </span>
                        </button>
                    ))}
                </div>
                <p className="mt-2 text-[11px] leading-4 text-gray-400">
                    Sorted by value. The reason list itself is numbered by the business — “10 ÜBRIGES”
                    belongs after “9 MENGENBONUS”, not between “1” and “2”; sorting the table below by
                    Reason uses that order.
                </p>
            </section>

            {/* ── Monthly stack ────────────────────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-sm font-semibold text-gray-900">Credited value by month</h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
                        {activeGroups.map(g => (
                            <span key={g.id} className="inline-flex items-center gap-1.5">
                                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: g.color }} />
                                {g.label}
                            </span>
                        ))}
                    </div>
                </div>
                <p className="mb-3 text-[11px] leading-4 text-gray-500">
                    EUR, stacked by reason group. The current month is dimmed — it is not comparable
                    until it closes.
                </p>

                <div className="overflow-x-auto">
                    <div className="flex min-w-[520px] items-end gap-1.5" style={{ height: 180 }}>
                        {trends.months.map(m => {
                            const groups = new Map<string, number>();
                            for (const [code, v] of Object.entries(m.byReason)) {
                                const g = groupOf(code);
                                groups.set(g, (groups.get(g) ?? 0) + v.valueEur);
                            }
                            const heightPct = (m.valueEur / maxMonthValue) * 100;
                            return (
                                <div key={m.month} className="flex min-w-[26px] flex-1 flex-col items-center gap-1">
                                    <div
                                        className="flex w-full flex-col-reverse justify-start"
                                        style={{ height: 150, opacity: m.isCurrentMonth ? 0.45 : 1 }}
                                        title={`${m.month}: ${formatAmountRounded(m.valueEur)} EUR · ${m.documents} documents${m.isCurrentMonth ? ' · month not closed' : ''}`}
                                    >
                                        <div className="w-full" style={{ height: `${heightPct}%` }}>
                                            <div className="flex h-full w-full flex-col-reverse overflow-hidden rounded-t">
                                                {REASON_GROUPS.filter(g => (groups.get(g.id) ?? 0) > 0).map(g => (
                                                    <div
                                                        key={g.id}
                                                        style={{
                                                            height: `${((groups.get(g.id) ?? 0) / Math.max(1, m.valueEur)) * 100}%`,
                                                            background: g.color,
                                                            // 2px surface gap between stacked segments.
                                                            marginTop: 2,
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <span className={`text-[10px] ${m.isCurrentMonth ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {m.month.slice(5)}
                                        {m.month.slice(5) === '01' && <span className="text-gray-400"> {m.month.slice(2, 4)}</span>}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {trends.months.some(m => m.isCurrentMonth) && (
                    <p className="mt-2 text-[11px] leading-4 text-gray-400">
                        The dimmed column is the month in progress. It is shown rather than hidden —
                        hiding it would lose the work done this month — but it must not be read as a drop.
                    </p>
                )}
            </section>

            {/* ── The same figures as a table ──────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">The same figures as a table</h2>
                        <p className="text-[11px] leading-4 text-gray-500">
                            Sortable, resizable, exportable — the charts are the overview, this is the answer.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {exportError && <span className="text-[11px] text-red-600">{exportError}</span>}
                        <button
                            onClick={runExport}
                            disabled={exporting}
                            className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            Export .xlsx
                        </button>
                    </div>
                </div>

                <div className="max-h-[420px] overflow-auto">
                    <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                        <colgroup>
                            {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                        </colgroup>
                        <thead>
                            <tr>
                                {COLS.map(c => (c.sort ? (
                                    <SortableHeader
                                        key={c.id}
                                        label={c.label}
                                        sortKey={c.sort}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align === 'right' ? 'right' : 'left'}
                                        className="shadow-[inset_0_-1px_0_rgb(229,231,235)]"
                                        onResizeStart={(x) => startResize(c.id, x)}
                                        onResetWidth={() => resetWidth(c.id)}
                                        isResizing={resizingKey === c.id}
                                    />
                                ) : (
                                    <th
                                        key={c.id}
                                        className={`sticky top-0 z-10 relative bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 shadow-[inset_0_-1px_0_rgb(229,231,235)] ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                                    >
                                        <span className="truncate">{c.label}</span>
                                        <ColumnResizeHandle
                                            label={c.label || c.id}
                                            active={resizingKey === c.id}
                                            onStart={(x) => startResize(c.id, x)}
                                            onReset={() => resetWidth(c.id)}
                                        />
                                    </th>
                                )))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map(r => (
                                <tr
                                    key={r.reasonCode}
                                    onClick={() => onDrillToReason(r.reasonCode)}
                                    className="cursor-pointer border-b border-gray-50 hover:bg-gray-50"
                                >
                                    <td className="truncate px-3 py-2 text-gray-800">
                                        <span
                                            className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle"
                                            style={{ background: groupColor(r.reasonCode) }}
                                        />
                                        {reasonLabel(catalog, r.reasonCode)}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                                        {formatCount(r.documents)}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                                        {formatCredited(r.valueEur)}
                                        {!r.valueEurIsComplete && (
                                            <span className="ml-1 text-[10px] text-amber-700" title="At least one document has no monthly FX rate, so its value is not in this total.">partial</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                                        {share(r).toFixed(1)}%
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                                        {r.documents ? formatAmountRounded(r.valueEur / r.documents) : '–'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="border-t border-gray-200 px-4 py-2.5 text-[11px] leading-4 text-gray-500">
                    {formatCount(trends.totals.documents)} credit notes ·{' '}
                    {formatAmountRounded(trends.totals.valueEur)} EUR credited in the selected period.
                    “No reason recorded” is a row of its own, not a missing one — it is the process gap at
                    dch and dit. Every figure here comes from the same evaluation as the list and its
                    tiles, so a row's click returns exactly the documents it counts.
                </p>
            </section>
        </div>
    );
}
